import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { useContent } from "@/context/ContentContext";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const LINKS = [
  { to: "/services", key: "nav.services", label: "Services" },
  { to: "/how-it-works", key: "nav.how", label: "Comment ça marche" },
  { to: "/faq", key: "nav.faq", label: "FAQ" },
  { to: "/contact", key: "nav.contact", label: "Contact" },
];

export function PublicNav() {
  const { user } = useAuth();
  const { t } = useContent();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <Logo alt="The World Promotions" className="h-9 w-9 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
          <span className="font-display text-lg font-bold tracking-tight">The World Promotions</span>
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label}`}
              className={`text-sm transition-colors hover:text-foreground ${loc.pathname === l.to ? "text-foreground font-medium" : "text-muted-foreground"}`}>
              {t(l.key, l.label)}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          {user ? (
            <Link to={user.role === "customer" ? "/dashboard" : "/admin"} data-testid="nav-dashboard"
              className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent">
              {t("nav.my_space", "Mon espace")}
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-muted-foreground hover:text-foreground">{t("nav.login", "Se connecter")}</Link>
              <Link to="/register" data-testid="nav-register" className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent">{t("nav.cta", "Commencer")}</Link>
            </>
          )}
        </div>
        <button data-testid="mobile-menu-btn" aria-label="menu" className="-mr-2 flex h-10 w-10 items-center justify-center rounded-lg md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-background px-5 py-4 md:hidden">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-3 text-base text-foreground/80">{t(l.key, l.label)}</Link>
          ))}
          <div className="mt-3 flex gap-3">
            <LanguageSwitcher variant="-m" />
          </div>
          <div className="mt-3 flex gap-3">
            {user ? (
              <Link to={user.role === "customer" ? "/dashboard" : "/admin"} className="flex-1 rounded-full bg-primary px-4 py-2 text-center text-sm text-primary-foreground">{t("nav.my_space", "Mon espace")}</Link>
            ) : (
              <>
                <Link to="/login" data-testid="mobile-nav-login" onClick={() => setOpen(false)} className="flex-1 rounded-full border border-border px-4 py-2.5 text-center text-sm font-medium text-foreground">{t("nav.login", "Se connecter")}</Link>
                <Link to="/register" data-testid="mobile-nav-register" onClick={() => setOpen(false)} className="flex-1 rounded-full bg-primary px-4 py-2.5 text-center text-sm font-medium text-primary-foreground">{t("nav.cta", "Commencer")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useContent();
  return (
    <footer className="border-t border-border bg-secondary/40 pb-20 md:pb-0">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 py-14 md:grid-cols-4">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2">
            <Logo alt="The World Promotions" className="h-9 w-9 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
            <span className="font-display font-bold">The World Promotions</span>
          </div>
          <p className="mt-3 max-w-xs text-sm text-muted-foreground">{t("footer.tagline", "Plateforme de promotion sur les réseaux sociaux avec livraison automatisée et suivi en temps réel.")}</p>
        </div>
        <FooterCol title={t("footer.col.platform", "Plateforme")} links={[["/services", t("nav.services", "Services")],["/how-it-works", t("nav.how", "Comment ça marche")],["/faq", t("nav.faq", "FAQ")]]} />
        <FooterCol title={t("footer.col.legal", "Légal")} links={[["/terms", t("footer.link.terms", "Conditions d'utilisation")],["/privacy", t("footer.link.privacy", "Confidentialité")],["/refund-policy", t("footer.link.refund", "Politique de remboursement")]]} />
        <FooterCol title={t("footer.col.company", "Entreprise")} links={[["/contact", t("nav.contact", "Contact")],["/login", t("nav.login", "Se connecter")],["/register", t("footer.link.register", "Créer un compte")]]} />
      </div>
      <div className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} The World Promotions. {t("footer.rights", "Tous droits réservés.")}
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="font-display text-sm font-semibold">{title}</h4>
      <ul className="mt-3 space-y-2">
        {links.map(([to, label]) => (
          <li key={to}><Link to={to} className="text-sm text-muted-foreground hover:text-foreground">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
